import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import { createDeployment } from '../api/environmentApi';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';

const inputCls = 'w-full border border-gray-300 dark:border-gray-600 rounded px-3 py-1.5 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500';
const labelCls = 'block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1';

const INITIAL = {
    name: '',
    description: '',
    type: 'azure' as const,
    domainBase: '',
    acr: { loginServer: '', resourceGroup: '', subscriptionId: '' },
    vault: { url: '', secretStoreName: '', tenantId: '' },
    environments: [] as any[],
    isDefault: false,
};

const EnvironmentCreatePage: React.FC = () => {
    const navigate = useNavigate();
    const [error, setError] = useState<string | null>(null);

    return (
        <div className="px-6 py-6 max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
                <button onClick={() => navigate('/environments')}
                    className="p-1.5 rounded text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                    <ArrowLeftIcon className="h-5 w-5" />
                </button>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">New Deployment</h1>
            </div>

            {error && (
                <div className="mb-4 p-3 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 text-sm">{error}</div>
            )}

            <Formik
                initialValues={INITIAL}
                onSubmit={async (values, { setSubmitting }) => {
                    setError(null);
                    try {
                        const r = await createDeployment(values);
                        navigate(`/environments/${r.data._id}`);
                    } catch (e: any) {
                        setError(e?.message ?? 'Failed to create deployment');
                    } finally {
                        setSubmitting(false);
                    }
                }}
            >
                {({ isSubmitting }) => (
                    <Form className="space-y-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                        {/* Basic info */}
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Basic information</h2>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className={labelCls}>Name *</label>
                                    <Field name="name" placeholder="e.g. Main, DR, Staging cluster" className={inputCls} required />
                                </div>
                                <div>
                                    <label className={labelCls}>Type</label>
                                    <Field as="select" name="type" className={inputCls}>
                                        <option value="azure">Azure</option>
                                        <option value="kubernetes">Kubernetes</option>
                                        <option value="docker">Docker</option>
                                        <option value="hybrid">Hybrid</option>
                                    </Field>
                                </div>
                                <div className="col-span-2">
                                    <label className={labelCls}>Description</label>
                                    <Field name="description" placeholder="Optional description" className={inputCls} />
                                </div>
                                <div className="col-span-2">
                                    <label className={labelCls}>Domain base</label>
                                    <Field name="domainBase" placeholder="e.g. ontoris.ch" className={inputCls} />
                                </div>
                            </div>
                        </div>

                        {/* ACR */}
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Azure Container Registry</h2>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className={labelCls}>Login server</label>
                                    <Field name="acr.loginServer" placeholder="myacr.azurecr.io" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Resource group</label>
                                    <Field name="acr.resourceGroup" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Subscription ID</label>
                                    <Field name="acr.subscriptionId" className={inputCls} />
                                </div>
                            </div>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1.5">
                                The Image Deploy page uses ACR_LOGIN_SERVER env var (or this value once linked).
                            </p>
                        </div>

                        {/* Vault */}
                        <div>
                            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-3">Vault (deployment-level)</h2>
                            <p className="text-xs text-gray-400 dark:text-gray-500 mb-3">
                                Shared vault for this deployment. Each environment can override with its own vault.
                            </p>
                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className={labelCls}>Vault URL</label>
                                    <Field name="vault.url" placeholder="https://my-vault.vault.azure.net" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Secret store name</label>
                                    <Field name="vault.secretStoreName" placeholder="azure-key-vault" className={inputCls} />
                                </div>
                                <div>
                                    <label className={labelCls}>Azure tenant ID</label>
                                    <Field name="vault.tenantId" placeholder="xxxxxxxx-..." className={inputCls} />
                                </div>
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Field type="checkbox" name="isDefault" id="isDefault" className="rounded border-gray-300" />
                            <label htmlFor="isDefault" className="text-sm text-gray-600 dark:text-gray-300">
                                Mark as default deployment
                            </label>
                        </div>

                        <p className="text-xs text-gray-400 dark:text-gray-500">
                            After creating the deployment, you can add environments (dev, staging, prod, …) with their own Keycloak, Vault, Redis, and cluster configs.
                        </p>

                        <div className="flex gap-3 pt-2">
                            <button type="submit" disabled={isSubmitting}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50">
                                <PlusIcon className="h-4 w-4" />
                                {isSubmitting ? 'Creating…' : 'Create Deployment'}
                            </button>
                            <button type="button" onClick={() => navigate('/environments')}
                                className="px-4 py-2 rounded text-sm font-medium border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
                                Cancel
                            </button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default EnvironmentCreatePage;
